/**
 * Domain enumerations shared by the API, the worker and the web app.
 * Values are stored as-is in PostgreSQL enums (see packages/db).
 */

export const ROLES = ['OWNER', 'ADMIN', 'SUPERVISOR', 'EMPLOYEE', 'CLIENT'] as const;
export type Role = (typeof ROLES)[number];
/** Roles that use the staff (admin) portal. */
export const STAFF_ROLES: readonly Role[] = ['OWNER', 'ADMIN', 'SUPERVISOR'];

export const PROPERTY_TYPES = ['HOUSE', 'CONDO', 'APARTMENT', 'COMMERCIAL', 'OTHER'] as const;
export type PropertyType = (typeof PROPERTY_TYPES)[number];

export const SERVICE_FREQUENCIES = ['ONE_TIME', 'WEEKLY', 'BIWEEKLY', 'MONTHLY', 'CUSTOM'] as const;
export type ServiceFrequency = (typeof SERVICE_FREQUENCIES)[number];

export const CONTRACT_TERMS = ['NONE', '6_MONTH', '12_MONTH'] as const;
export type ContractTerm = (typeof CONTRACT_TERMS)[number];

/** How the client is billed. Deliberately independent from ServiceFrequency. */
export const BILLING_FREQUENCIES = ['ONE_TIME', 'RECURRING_MONTHLY', 'CONTRACT_6_MONTH', 'CONTRACT_12_MONTH'] as const;
export type BillingFrequency = (typeof BILLING_FREQUENCIES)[number];

export const QUOTE_STATUSES = [
  'DRAFT',
  'SUBMITTED',
  'UNDER_REVIEW',
  'PRICE_PROPOSED',
  'SENT_TO_CLIENT',
  'ACCEPTED',
  'REJECTED',
  'EXPIRED',
  'CANCELLED',
] as const;
export type QuoteStatus = (typeof QUOTE_STATUSES)[number];

export const JOB_STATUSES = [
  'DRAFT',
  'UNASSIGNED',
  'ASSIGNED',
  'CONFIRMED',
  'EN_ROUTE',
  'ARRIVED',
  'IN_PROGRESS',
  'COMPLETED',
  'REVIEW_REQUIRED',
  'CANCELLED',
  'NO_SHOW',
] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

export const JOB_PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const;
export type JobPriority = (typeof JOB_PRIORITIES)[number];

export const AGREEMENT_STATUSES = ['ACTIVE', 'PAUSED', 'CANCELLED', 'ENDED'] as const;
export type AgreementStatus = (typeof AGREEMENT_STATUSES)[number];

export const COMPENSATION_METHODS = ['FIXED_PER_JOB', 'HOURLY', 'PERCENTAGE', 'CUSTOM'] as const;
export type CompensationMethod = (typeof COMPENSATION_METHODS)[number];

export const PAYROLL_STATUSES = ['PENDING', 'APPROVED', 'PAID', 'VOID'] as const;
export type PayrollStatus = (typeof PAYROLL_STATUSES)[number];

export const PAYROLL_PERIOD_STATUSES = ['OPEN', 'CLOSED', 'PAID'] as const;
export type PayrollPeriodStatus = (typeof PAYROLL_PERIOD_STATUSES)[number];

export const PAYMENT_STATUSES = [
  'PENDING',
  'AUTHORIZED',
  'PAID',
  'FAILED',
  'REFUNDED',
  'PARTIALLY_REFUNDED',
  'CANCELLED',
] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const SUBSCRIPTION_STATUSES = ['ACTIVE', 'TRIALING', 'PAST_DUE', 'CANCELLED', 'EXPIRED'] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export const CONVERSATION_STATUSES = ['OPEN', 'WAITING_CLIENT', 'WAITING_INTERNAL', 'RESOLVED', 'ARCHIVED'] as const;
export type ConversationStatus = (typeof CONVERSATION_STATUSES)[number];

export const CONVERSATION_KINDS = ['CLIENT_SUPPORT', 'EMPLOYEE_SUPPORT', 'INTERNAL'] as const;
export type ConversationKind = (typeof CONVERSATION_KINDS)[number];

export const CONVERSATION_PRIORITIES = ['LOW', 'NORMAL', 'HIGH'] as const;
export type ConversationPriority = (typeof CONVERSATION_PRIORITIES)[number];

export const MESSAGE_KINDS = ['MESSAGE', 'INTERNAL_NOTE', 'SYSTEM'] as const;
export type MessageKind = (typeof MESSAGE_KINDS)[number];

export const NOTIFICATION_CHANNELS = ['EMAIL', 'IN_APP'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export const NOTIFICATION_QUEUE_STATUSES = ['PENDING', 'PROCESSING', 'SENT', 'FAILED', 'CANCELLED'] as const;
export type NotificationQueueStatus = (typeof NOTIFICATION_QUEUE_STATUSES)[number];

export const NOTIFICATION_TYPES = [
  'CLIENT_ACCOUNT_CREATED',
  'QUOTE_AVAILABLE',
  'QUOTE_ACCEPTED',
  'BOOKING_CONFIRMED',
  'BOOKING_CHANGED',
  'BOOKING_CANCELLED',
  'JOB_REMINDER',
  'JOB_COMPLETED',
  'PAYMENT_RECEIVED',
  'PAYMENT_FAILED',
  'EMPLOYEE_JOB_ASSIGNED',
  'EMPLOYEE_SCHEDULE_CHANGED',
  'EMPLOYEE_JOB_CANCELLED',
  'EMPLOYEE_DAILY_SUMMARY',
  'NEW_MESSAGE',
  'PASSWORD_RESET',
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export const CUTOFF_MODES = ['HOURS', 'BUSINESS_DAY'] as const;
export type CutoffMode = (typeof CUTOFF_MODES)[number];

export const EMPLOYEE_STATUSES = ['ACTIVE', 'INVITED', 'ON_LEAVE', 'INACTIVE'] as const;
export type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number];

export const ACCESS_INSTRUCTION_KINDS = ['DOOR_CODE', 'KEY', 'ALARM', 'LOCKBOX', 'CONCIERGE', 'OTHER'] as const;
export type AccessInstructionKind = (typeof ACCESS_INSTRUCTION_KINDS)[number];

export const PAYROLL_PERIOD_KINDS = ['WEEKLY', 'BIWEEKLY', 'SEMI_MONTHLY', 'MONTHLY'] as const;
export type PayrollPeriodKind = (typeof PAYROLL_PERIOD_KINDS)[number];
